'use client';

import { c as _c } from "react/compiler-runtime";
import NextImage, { type ImageProps, type StaticImageData } from 'next/image';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { IMAGE_PLACEHOLDER } from '@/constants';
const CDN_HOST = 'cdn.welfarehello.com';
const loadedImageSources = new Set<string>();

/** 외부 이미지 (CDN이 아닌) 여부 확인 */
const isExternalNonCdnImage = (src: string | StaticImageData | undefined): boolean => {
  if (!src || typeof src !== 'string') return false;
  if (!src.startsWith('http://') && !src.startsWith('https://')) return false;
  try {
    const url = new URL(src);
    return !url.hostname.includes(CDN_HOST);
  } catch {
    return false;
  }
};
const getImageCacheKey = (src: string | StaticImageData | undefined) => {
  if (!src) return;
  return typeof src === 'string' ? src : src.src;
};
interface CustomImageBaseProps extends Omit<ImageProps, 'src' | 'className' | 'onError' | 'onLoad' | 'width' | 'height' | 'fill'> {
  src?: string | StaticImageData;
  alt: string;
  fallbackSrc?: string;
  className?: string;
  onError?: (error: React.SyntheticEvent<HTMLImageElement>) => void;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}
type CustomImageProps = (CustomImageBaseProps & {
  fill: true;
  width?: number;
  height?: number;
}) | (CustomImageBaseProps & {
  fill?: false;
  width: number;
  height: number;
});
export const Image = memo(t0 => {
  const $ = _c(37);
  let alt;
  let className;
  let onError;
  let onLoad;
  let preload;
  let props;
  let src;
  let t1;
  let t2;
  let t3;
  let unoptimized;
  if ($[0] !== t0) {
    ({
      src,
      alt,
      fallbackSrc: t1,
      quality: t2,
      loading: t3,
      className,
      onError,
      onLoad,
      preload,
      unoptimized,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = className;
    $[3] = onError;
    $[4] = onLoad;
    $[5] = preload;
    $[6] = props;
    $[7] = src;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = unoptimized;
  } else {
    alt = $[1];
    className = $[2];
    onError = $[3];
    onLoad = $[4];
    preload = $[5];
    props = $[6];
    src = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    unoptimized = $[11];
  }
  const fallbackSrc = t1 === undefined ? IMAGE_PLACEHOLDER : t1;
  const quality = t2 === undefined ? 50 : t2;
  const loading = t3 === undefined ? "lazy" : t3;
  const shouldPreload = preload;
  const [hasError, setHasError] = useState(false);
  const displaySrc = hasError ? fallbackSrc : src || fallbackSrc;
  let t4;
  if ($[12] !== displaySrc) {
    t4 = getImageCacheKey(displaySrc);
    $[12] = displaySrc;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const cacheKey = t4;
  const hasLoadedBefore = cacheKey ? loadedImageSources.has(cacheKey) : false;
  let t5;
  if ($[14] !== displaySrc || $[15] !== unoptimized) {
    t5 = unoptimized ?? isExternalNonCdnImage(displaySrc);
    $[14] = displaySrc;
    $[15] = unoptimized;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const shouldUnoptimize = t5;
  let t6;
  if ($[17] !== hasError || $[18] !== onError) {
    t6 = error => {
      if (!hasError) {
        setHasError(true);
        onError?.(error);
      }
    };
    $[17] = hasError;
    $[18] = onError;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleError = t6;
  let t7;
  if ($[20] !== cacheKey || $[21] !== onLoad) {
    t7 = event => {
      if (cacheKey) {
        loadedImageSources.add(cacheKey);
      }
      onLoad?.(event);
    };
    $[20] = cacheKey;
    $[21] = onLoad;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleLoad = t7;
  const resolvedLoading = preload || loading !== "lazy" || !hasLoadedBefore ? loading : "eager";
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setHasError(false);
    };
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== src) {
    t9 = [src];
    $[24] = src;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  useEffect(t8, t9);
  const t10 = shouldPreload ? undefined : resolvedLoading;
  let t11;
  if ($[26] !== alt || $[27] !== className || $[28] !== displaySrc || $[29] !== handleError || $[30] !== handleLoad || $[31] !== props || $[32] !== quality || $[33] !== shouldPreload || $[34] !== shouldUnoptimize || $[35] !== t10) {
    t11 = <NextImage quality={quality} loading={t10} preload={shouldPreload} unoptimized={shouldUnoptimize} {...props} alt={alt} className={className} src={displaySrc} onError={handleError} onLoad={handleLoad} />;
    $[26] = alt;
    $[27] = className;
    $[28] = displaySrc;
    $[29] = handleError;
    $[30] = handleLoad;
    $[31] = props;
    $[32] = quality;
    $[33] = shouldPreload;
    $[34] = shouldUnoptimize;
    $[35] = t10;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
});
Image.displayName = 'Image';